import { ref, watch, computed } from '@vue/composition-api';
import store from '@/store';

// Notification
import { useToast } from 'vue-toastification/composition';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import { formatCurrency } from '@/utils/functions';
import moment from 'moment';
import invoiceStoreModule from '../invoiceStoreModule';

export default function useInvoicesList() {
  // Use toast
  const toast = useToast();

  const refInvoiceListTable = ref(null);

  // Table Handlers
  const tableColumns = [
    { key: 'select', label: '', sortable: false },
    { key: 'id', label: '#', sortable: false },
    { key: 'docNo', sortable: false, thStyle: { minWidth: '120px' }, stickyColumn: true },
    { key: 'customer', sortable: false },
    { key: 'customerNtn', label: 'NTN', sortable: false, thStyle: { minWidth: '120px' } },
    { key: 'customerCnic', label: 'CNIC', sortable: false, thStyle: { minWidth: '140px' } },
    { key: 'salesType', sortable: false, thStyle: { minWidth: '200px' } },
    { key: 'fbrStatus', sortable: false, thStyle: { minWidth: '150px' } },
    {
      key: 'tranDate',
      sortable: false,
      formatter: (val) => moment(val).format('DD MMM, YYYY'),
      thStyle: { minWidth: '160px' },
    },
    {
      key: 'netAmount',
      sortable: false,
      formatter: (val) => `Rs. ${formatCurrency(val)}`,
      thStyle: { minWidth: '160px' },
    },
    {
      key: 'valWithoutTax',
      label: 'Value',
      sortable: false,
      formatter: (val) => `Rs. ${formatCurrency(val)}`,
      thStyle: { minWidth: '160px' },
    },
    {
      key: 'totalTax',
      sortable: false,
      formatter: (val) => `Rs. ${formatCurrency(val)}`,
      thStyle: { minWidth: '160px' },
    },
    {
      key: 'totalSalesTax',
      label: 'Sales Tax',
      sortable: false,
      formatter: (val) => `Rs. ${formatCurrency(val)}`,
      thStyle: { minWidth: '160px' },
    },
    {
      key: 'notifiedValue',
      label: 'Fixed/Notified',
      sortable: false,
      formatter: (val) => `Rs. ${formatCurrency(val)}`,
      thStyle: { minWidth: '160px' },
    },
    {
      key: 'totalFurtherTax',
      label: 'Further Tax',
      sortable: false,
      formatter: (val) => `Rs. ${formatCurrency(val)}`,
      thStyle: { minWidth: '160px' },
    },
    {
      key: 'totalAdvanceTax',
      label: 'Additional Tax',
      sortable: false,
      formatter: (val) => `Rs. ${formatCurrency(val)}`,
      thStyle: { minWidth: '160px' },
    },

    {
      key: 'addedOn',
      sortable: false,
      formatter: (val) => moment(val).format('DD MMM, YYYY'),
      thStyle: { minWidth: '160px' },
    },
    {
      key: 'updatedOn',
      sortable: false,
      formatter: (val) => moment(val).format('DD MMM, YYYY'),
      thStyle: { minWidth: '160px' },
    },
    { key: 'actions', stickyColumn: true },
  ];

  const items = ref([]);
  const loading = ref(false);
  const perPage = ref(10);
  const totalInvoices = ref(0);
  const currentPage = ref(1);
  const perPageOptions = [10, 25, 50, 100];
  const searchQuery = ref('');
  const sortBy = ref('id');
  const isSortDirDesc = ref(true);
  const selectedRows = ref([]);
  const selectAll = ref(false);
  const fetching = ref(false);

  const isActiveFilter = ref(null);
  const fbrStatusFilter = ref(null);
  const customerIDFilter = ref(null);
  const salesTypeIDFilter = ref(null);
  const fbrInvoiceNumberFilter = ref(null);
  const saleDateFrom = ref(moment().startOf('month').format('YYYY-MM-DD'));
  const saleDateTo = ref(moment().endOf('month').format('YYYY-MM-DD'));

  const dataMeta = computed(() => {
    const localItemsCount = refInvoiceListTable.value
      ? refInvoiceListTable.value.localItems.length
      : 0;
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalInvoices.value,
    };
  });

  const refetchData = () => {
    fetchInvoices();
  };

  watch(
    [currentPage, perPage],
    () => {
      refetchData();
    }
  );

  watch(
    [
      searchQuery,
      fbrStatusFilter,
      customerIDFilter,
      salesTypeIDFilter,
      fbrInvoiceNumberFilter,
      saleDateFrom,
      saleDateTo,
    ],
    () => {
      if (selectAll.value) {
        selectAll.value = false;
      }
      selectedRows.value = [];
      if (refInvoiceListTable.value) {
        refInvoiceListTable.value.clearSelected();
      }
      refetchData();
    }
  );

  const selectedSummary = computed(() => {
    let pendingCount = 0;
    let invalidCount = 0;
    let postedCount = 0;
    selectedRows.value.forEach((row) => {
      if (row.fbrStatus === 'pending' || row.fbrStatus === null) pendingCount++;
      else if (row.fbrStatus === 'Invalid') invalidCount++;
      else if (row.fbrStatus && row.fbrStatus.toString().toLowerCase() === 'posted') postedCount++;
    });
    return {
      total: pendingCount + invalidCount + postedCount,
      pendingCount,
      invalidCount,
      postedCount
    };
  });

  const toggleSelectAll = async () => {
    if (selectAll.value) {
      fetching.value = true;
      try {
        const INVOICE_APP_STORE_MODULE_NAME = 'sales-invoice';
        if (!store.hasModule(INVOICE_APP_STORE_MODULE_NAME)) {
          store.registerModule(INVOICE_APP_STORE_MODULE_NAME, invoiceStoreModule);
        }

        const response = await store.dispatch('sales-invoice/fetchInvoices', {
          pageNumber: 1,
          pageSize: 99999, // Get all available data based on filter
          searchTerm: searchQuery.value,

          fbrInvoiceNumber: fbrInvoiceNumberFilter.value,
          fbrStatus: fbrStatusFilter.value,
          customerID: customerIDFilter.value,
          salesTypeID: salesTypeIDFilter.value,
          saleDateFrom: saleDateFrom.value,
          saleDateTo: saleDateTo.value,
        });

        const allItems = response.items || [];
        const eligibleItems = allItems;

        selectedRows.value = eligibleItems;

        // Automatically select the UI component rendering rows on the current page
        if (refInvoiceListTable.value && refInvoiceListTable.value.localItems) {
          const pageEligibleRows = refInvoiceListTable.value.localItems;
          pageEligibleRows.forEach((row) => {
            const index = refInvoiceListTable.value.localItems.findIndex((r) => r.id === row.id);
            if (index !== -1) {
              refInvoiceListTable.value.selectRow(index);
            }
          });
        }
      } catch (error) {
        console.error('Error fetching all items for Select All:', error);
      } finally {
        fetching.value = false;
      }
    } else {
      selectedRows.value = [];
      if (refInvoiceListTable.value) {
        refInvoiceListTable.value.clearSelected();
      }
    }
  };

  const fetchInvoices = () => {
    const INVOICE_APP_STORE_MODULE_NAME = 'sales-invoice';
    if (!store.hasModule(INVOICE_APP_STORE_MODULE_NAME)) {
      store.registerModule(INVOICE_APP_STORE_MODULE_NAME, invoiceStoreModule);
    }

    loading.value = true;

    store
      .dispatch('sales-invoice/fetchInvoices', {
        pageNumber: currentPage.value,
        pageSize: perPage.value,
        searchTerm: searchQuery.value,

        fbrInvoiceNumber: fbrInvoiceNumberFilter.value,
        fbrStatus: fbrStatusFilter.value,
        customerID: customerIDFilter.value,
        salesTypeID: salesTypeIDFilter.value,
        saleDateFrom: saleDateFrom.value,
        saleDateTo: saleDateTo.value,
      })
      .then((response) => {
        const { items: responseItems, pagination } = response;

        items.value = [...responseItems];
        // callback(items);
        totalInvoices.value = pagination.totalCount;

        // Re-apply selection for local items if "Select All" is active
        if (selectAll.value) {
          setTimeout(() => {
            if (refInvoiceListTable.value && refInvoiceListTable.value.localItems) {
              const pageEligibleRows = refInvoiceListTable.value.localItems;
              pageEligibleRows.forEach((row) => {
                const index = refInvoiceListTable.value.localItems.findIndex((r) => r.id === row.id);
                if (index !== -1) {
                  refInvoiceListTable.value.selectRow(index);
                }
              });
            }
          }, 100);
        }
      })
      .catch((error) => {
        console.error('=== useInvoiceList fetchInvoices() error', error);

        toast({
          component: ToastificationContent,
          props: {
            title: 'Error fetching invoice list',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        });
      })
      .finally(() => {
        loading.value = false;
        fetching.value = false;
      });
  };

  return {
    loading,
    items,
    tableColumns,
    perPage,
    currentPage,
    totalInvoices,
    dataMeta,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refInvoiceListTable,
    selectedRows,
    selectAll,
    selectedSummary,

    isActiveFilter,
    fbrStatusFilter,
    customerIDFilter,
    salesTypeIDFilter,
    fbrInvoiceNumberFilter,
    saleDateFrom,
    saleDateTo,

    fetchInvoices,
    refetchData,
    toggleSelectAll,
  };
}
